"use client";

import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import usePatientAvailability from "../../hooks/usePatientAvailability";
import "./PatientRescheduleModal.css";

/* ── helpers ── */
function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

const MONTHS_EN = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const MONTHS_FR = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const MONTHS_AR = ["يناير","فبراير","مارس","أبريل","مايو","يونيو","يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"];
const DAYS_EN   = ["Su","Mo","Tu","We","Th","Fr","Sa"];
const DAYS_FR   = ["Di","Lu","Ma","Me","Je","Ve","Sa"];
const DAYS_AR   = ["أح","إث","ثل","أر","خم","جم","سب"];

const toDateKey = (dateObj) => {
  if (!(dateObj instanceof Date)) return "";
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, "0");
  const d = String(dateObj.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const formatTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const toFullISO = (timeValue, dateKey) => {
  if (!timeValue) return null;
  const str = String(timeValue);
  if (str.includes("T")) {
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  if (dateKey) {
    const time = str.length === 5 ? `${str}:00` : str;
    const combined = new Date(`${dateKey}T${time}`);
    return Number.isNaN(combined.getTime()) ? null : combined.toISOString();
  }
  return null;
};

function useCalendarLabels() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2);
  const months = lang === "ar" ? MONTHS_AR : lang === "fr" ? MONTHS_FR : MONTHS_EN;
  const days   = lang === "ar" ? DAYS_AR   : lang === "fr" ? DAYS_FR   : DAYS_EN;
  return { months, days };
}

/**
 * Extracts the doctor/medecin ID from an appointment object.
 * Tries every field the backend might populate.
 */
function extractDoctorId(appointment) {
  return (
    appointment?.medecinId    ||
    appointment?.doctorId     ||
    appointment?.doctor?.id   ||
    appointment?.doctor?.pgId ||
    appointment?.doctor?._id  ||
    appointment?.providerId   ||
    null
  );
}

/* ================================================================
   PatientRescheduleModal
   Props:
     appointment  – the appointment object from UpcomingAppointments
     onClose      – () => void
     onReschedule – ({ appointment, date, slot, startTime, endTime }) => Promise<void>
================================================================ */
export default function PatientRescheduleModal({ appointment, onClose, onReschedule }) {
  const { t } = useTranslation("appointments");
  const { months, days } = useCalendarLabels();
  const { i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith("ar");

  const [isSubmitting, setIsSubmitting]       = useState(false);
  const [rescheduleError, setRescheduleError] = useState(null);

  const today = new Date();
  const [calYear, setCalYear]   = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [selDate, setSelDate]   = useState(null);
  const [selSlot, setSelSlot]   = useState(null);

  const { slotsByKey, loadingSlots, slotsError, loadSlots } = usePatientAvailability();

  // ── Resolve doctor ID from every possible field ──────────────────
  const doctorId = extractDoctorId(appointment);

  const providerName =
    appointment?.providerDisplay ||
    appointment?.medecinName     ||
    appointment?.doctorName      ||
    appointment?.provider        ||
    t("manageModal.unknownDoctor", "Doctor");

  const apptDateLabel = appointment?.startTime
    ? new Date(appointment.startTime).toLocaleDateString()
    : appointment?.date || "—";

  const selectedDateKey = selDate ? toDateKey(selDate) : "";
  const slotsKey        = doctorId && selectedDateKey ? `${doctorId}|${selectedDateKey}` : "";
  const availableSlots  = slotsByKey?.[slotsKey] || [];

  /* ── calendar cells ── */
  const daysInMonth  = getDaysInMonth(calYear, calMonth);
  const firstWeekDay = getFirstDayOfMonth(calYear, calMonth);
  const prevMonthDays = getDaysInMonth(calYear, calMonth - 1);

  const cells = useMemo(() => {
    const result = [];
    for (let i = 0; i < firstWeekDay; i++)
      result.push({ day: prevMonthDays - firstWeekDay + 1 + i, ghost: true });
    for (let d = 1; d <= daysInMonth; d++)
      result.push({ day: d, ghost: false, dateObj: new Date(calYear, calMonth, d) });
    const remaining = 42 - result.length;
    for (let d = 1; d <= remaining; d++)
      result.push({ day: d, ghost: true });
    return result;
  }, [calYear, calMonth, daysInMonth, firstWeekDay, prevMonthDays]);

  const isPast = (dateObj) => {
    if (!dateObj) return false;
    const t = new Date(); t.setHours(0, 0, 0, 0);
    return dateObj < t;
  };
  const isToday = (dateObj) => {
    if (!dateObj) return false;
    const n = new Date();
    return dateObj.getDate() === n.getDate() && dateObj.getMonth() === n.getMonth() && dateObj.getFullYear() === n.getFullYear();
  };
  const isSelected = (dateObj) =>
    dateObj && selDate ? dateObj.toDateString() === selDate.toDateString() : false;

  const prevMonth = () => {
    setSelDate(null); setSelSlot(null);
    if (calMonth === 0) { setCalMonth(11); setCalYear((y) => y - 1); }
    else setCalMonth((m) => m - 1);
  };
  const nextMonth = () => {
    setSelDate(null); setSelSlot(null);
    if (calMonth === 11) { setCalMonth(0); setCalYear((y) => y + 1); }
    else setCalMonth((m) => m + 1);
  };

  const handleSelectDate = async (dateObj) => {
    if (!dateObj || isPast(dateObj)) return;
    setSelDate(dateObj);
    setSelSlot(null);
    setRescheduleError(null);
    if (doctorId) await loadSlots(doctorId, toDateKey(dateObj));
  };

  const handleConfirm = async () => {
    if (!selDate || !selSlot || isSubmitting) return;
    setRescheduleError(null);
    setIsSubmitting(true);
    try {
      const startTime = toFullISO(selSlot.startTime, selectedDateKey);
      const endTime   = toFullISO(selSlot.endTime,   selectedDateKey);
      if (!startTime) throw new Error(`startTime invalide — reçu: "${selSlot.startTime}", date: "${selectedDateKey}"`);
      if (!endTime)   throw new Error(`endTime invalide — reçu: "${selSlot.endTime}", date: "${selectedDateKey}"`);

      await onReschedule({ appointment, date: selectedDateKey, slot: selSlot, startTime, endTime });
      onClose();
    } catch (err) {
      console.error("❌ [PatientRescheduleModal] reschedule failed:", err);
      setRescheduleError(err?.message || t("manageModal.slotsError", "Erreur lors du replanification."));
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ================================================================ RENDER */
  return (
    <>
      <div className="prm-overlay" onClick={onClose} />
      <div className="prm-modal" dir={isRTL ? "rtl" : "ltr"}>

        {/* header */}
        <div className="prm-header">
          <div>
            <p className="prm-header-title">
              {t("manageModal.reschedule", "Reschedule appointment")}
            </p>
            <p className="prm-header-sub">
              Dr. {providerName} • {apptDateLabel}
            </p>
          </div>
          <button type="button" className="prm-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* no doctor ID warning */}
        {!doctorId && (
          <div className="prm-warning">
            {t("manageModal.noDoctorIdWarning", "Doctor ID is missing — cannot load slots.")}
          </div>
        )}

        {/* ── Calendar ── */}
        <p className="prm-section-label">{t("manageModal.selectDate", "Select a date")}</p>
        <div className="prm-calendar">
          <div className="prm-cal-nav">
            <button type="button" className="prm-cal-nav-btn" onClick={prevMonth}>‹</button>
            <span className="prm-cal-month-label">{months[calMonth]} {calYear}</span>
            <button type="button" className="prm-cal-nav-btn" onClick={nextMonth}>›</button>
          </div>
          <div className="prm-cal-grid">
            {days.map((d) => <div key={d} className="prm-cal-day-header">{d}</div>)}
            {cells.map((c, i) => {
              const past = !c.ghost && c.dateObj && isPast(c.dateObj);
              const tod  = !c.ghost && c.dateObj && isToday(c.dateObj);
              const sel  = !c.ghost && c.dateObj && isSelected(c.dateObj);
              return (
                <div
                  key={i}
                  onClick={() => !c.ghost && !past && handleSelectDate(c.dateObj)}
                  className={[
                    "prm-cal-cell",
                    c.ghost ? "is-ghost" : "",
                    past    ? "is-past"  : "",
                    tod     ? "is-today" : "",
                    sel     ? "is-selected" : "",
                  ].filter(Boolean).join(" ")}
                >
                  {c.day}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Time slots ── */}
        <p className="prm-section-label">{t("manageModal.selectTime", "Select a time slot")}</p>

        {loadingSlots && (
          <div className="prm-slots-state">{t("manageModal.loadingSlots", "Loading slots…")}</div>
        )}
        {!!slotsError && !loadingSlots && (
          <div className="prm-warning">{t("manageModal.slotsError", "Error loading slots.")}</div>
        )}
        {!selDate && !loadingSlots && (
          <div className="prm-slots-state">{t("manageModal.selectDateFirst", "Select a date first.")}</div>
        )}
        {selDate && !loadingSlots && !doctorId && (
          <div className="prm-slots-state">{t("manageModal.noDoctorId", "No doctor ID.")}</div>
        )}
        {selDate && doctorId && !loadingSlots && availableSlots.length === 0 && !slotsError && (
          <div className="prm-slots-state">{t("manageModal.noSlots", "No slots available for this date.")}</div>
        )}
        {selDate && doctorId && availableSlots.length > 0 && (
          <div className="prm-time-grid">
            {availableSlots.map((slot) => (
              <button
                type="button"
                key={slot.id || slot.startTime}
                onClick={() => { setSelSlot(slot); setRescheduleError(null); }}
                className={`prm-time-slot ${selSlot?.startTime === slot.startTime ? "is-selected" : ""}`}
              >
                {formatTime(
                  slot.startTime?.includes("T")
                    ? slot.startTime
                    : `${selectedDateKey}T${slot.startTime}`
                )}
              </button>
            ))}
          </div>
        )}

        {rescheduleError && (
          <div className="prm-warning" style={{ marginTop: 8 }}>{rescheduleError}</div>
        )}

        {/* ── Footer ── */}
        <div className="prm-footer">
          <button type="button" className="prm-back-btn" onClick={onClose}>
            {t("manageModal.back", "Back")}
          </button>
          <button
            type="button"
            className="prm-confirm-btn"
            disabled={!selDate || !selSlot || isSubmitting}
            onClick={handleConfirm}
          >
            {isSubmitting
              ? t("manageModal.rescheduling", "Rescheduling…")
              : t("manageModal.confirmReschedule", "Confirm")}
          </button>
        </div>
      </div>
    </>
  );
}